import { inject, Injectable } from '@angular/core';
import { Constants } from '@core/app-constants';
import {
  mapToPrintAction,
  PrintableFileResponse,
  PrintingPreviewInfo,
  TicketScopeEnum,
} from '@core/models/printing/print.model';

import {
  BocaLogoResponseDto,
  GetBocaTicketLogosResponse,
  DownloadTypeEnumRequestDto,
  EventSessionScopePrintJobResponseDto,
  GetEventSessionScopePrintJobsRequest,
  GetEventSessionScopePrintJobsResponse,
  GetSubscriptionScopePrintJobsRequest,
  GetSubscriptionScopePrintJobsResponse,
  GetEventSessionScopeSeatsRequest,
  GetEventSessionScopeSeatsResponse,
  GetSubscriptionScopeSeatsRequest,
  GetSubscriptionScopeSeatsResponse,
  EventSessionScopeSeatsResponseDto,
  FileResponse,
  OrderClient,
  OrderSeatStatusEnumRequestDto,
  OrderStatusEnumRequestDto,
  PrintActionEnumRequestDto,
  PrintClient,
  PrintFilterRequestDto,
  PrintingClient,
  PrintTicketActionEnumRequestDto,
  PrintTicketTypeEnumResponseDto,
  SalesBocaTicketResponseDto,
  SubscriptionCardResponseDto,
  SubscriptionScopePrintJobResponseDto,
  UpdateOrderItemStatusResponse,
  UpdateOrderSeatStatusResponse,
  SubscriptionScopeSeatsResponseDto,
  LaunchJobPrintPdfTicketResponse,
} from '@core/services/apiclient';
import { AbstractPrintService } from '@core/services/printing/abstract-print.service';
import { catchError, map, Observable, throwError } from 'rxjs';
import { AlertService } from '../alert.service';
import { HttpHandlerService } from '../http-handler.service';

@Injectable({
  providedIn: 'root',
})
export class SharedPrintTicketService extends AbstractPrintService {
  private readonly orderClient = inject(OrderClient);
  private readonly printClient = inject(PrintClient);
  private readonly printingClient = inject(PrintingClient);
  private readonly httpHandlerService = inject(HttpHandlerService);
  private readonly alertService = inject(AlertService);

  public getName(): string {
    return 'shared';
  }

  public getCardPreviewSeats(cardInfos: SubscriptionScopeSeatsResponseDto[]): PrintingPreviewInfo[] {
    if (!cardInfos) {
      return [];
    }

    return cardInfos.map((cardInfo) => ({
      scope: TicketScopeEnum.Subscription,
      previewType: 'image',
      id: cardInfo.physicalSeatId,
      checked: true,
      subscriptionName: cardInfo.subscriptionName,
      subscriptionId: cardInfo.subscriptionId,
      subscriptionOrderId: cardInfo.subscriptionOrderId,
      orderId: cardInfo.orderId,
      rank: cardInfo.rank,
      sessionId: cardInfo.subscriptionId,
      seat: cardInfo.seatNumber,
      seatId: cardInfo.physicalSeatId,
      ticketTypes: [PrintTicketTypeEnumResponseDto.Card],
    }));
  }

  public previewEventSessionTicket(
    printType: PrintTicketTypeEnumResponseDto,
    seatId: number,
  ): Observable<FileResponse> {
    switch (printType) {
      case PrintTicketTypeEnumResponseDto.Pdf:
        return this.printClient
          .jobPreviewPdfTicket(
            {
              action: null,
              downloadType: DownloadTypeEnumRequestDto.Pdf,
              orderId: null,
              orderItemIds: null,
              seatIds: [seatId],
            },
            Constants.ApiVersion,
          )
          .pipe(
            this.httpHandlerService.handleApiResponse<FileResponse>(),
            map((response: FileResponse | null) => {
              if (!response?.data || response.status === 204) {
                return null;
              }
              return response;
            }),
          );

      case PrintTicketTypeEnumResponseDto.Boca:
        return this.previewBoca(seatId);
    }
  }

  public previewSubscriptionTicket(
    physicalSeatId: number,
    subscriptionOrderId: number,
    printAction: PrintTicketActionEnumRequestDto = PrintTicketActionEnumRequestDto.Print,
  ): Observable<FileResponse> {
    return this.previewCard(physicalSeatId, subscriptionOrderId, printAction);
  }

  public getSubscriptionScopeSeats(
    request: GetSubscriptionScopeSeatsRequest,
  ): Observable<SubscriptionScopeSeatsResponseDto[]> {
    return this.printingClient
      .getSubscriptionScopeTicketTypeSeats(request, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<GetSubscriptionScopeSeatsResponse>(),
        map((response) => response.seats ?? []),
      );
  }

  public getEventSessionScopeSeats(
    request: GetEventSessionScopeSeatsRequest,
  ): Observable<EventSessionScopeSeatsResponseDto[]> {
    return this.printingClient
      .getEventSessionScopeTicketTypeSeats(request, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<GetEventSessionScopeSeatsResponse>(),
        map((response) => response.seats ?? []),
      );
  }

  public printAsPdf(options: PrintFilterRequestDto): Observable<PrintableFileResponse> {
    if (options.downloadType === DownloadTypeEnumRequestDto.Zip) {
      return this.printClient.jobPrintZipPdfTickets(options, Constants.ApiVersion).pipe(
        this.httpHandlerService.handleApiResponse<LaunchJobPrintPdfTicketResponse>(),
        map(
          (): PrintableFileResponse => ({
            data: new Blob(),
            status: 202,
            fileName: undefined,
            headers: {},
            isJob: true,
          }),
        ),
        catchError(this.handlePrintError.bind(this)),
      );
    }

    return this.printClient.jobPrintPdfTicket(options, Constants.ApiVersion).pipe(
      this.httpHandlerService.handleApiResponse<LaunchJobPrintPdfTicketResponse>(),
      map(
        (): PrintableFileResponse => ({
          data: new Blob(),
          status: 202,
          fileName: undefined,
          headers: {},
          isJob: true,
        }),
      ),
      catchError(this.handlePrintError.bind(this)),
    );
  }

  private handlePrintError(error: unknown): Observable<never> {
    this.alertService.showApiError(error, 'printing.editionAlreadyInProgress');
    return throwError(() => error);
  }

  public sendByEmail(options: {
    action?: PrintActionEnumRequestDto;
    orderId?: number;
    orderItemIds?: number[];
    seatIds?: number[];
    subscriptionOrderIds?: number[];
    email?: string;
  }): Observable<void> {
    const filter = {
      orderId: options.orderId,
      orderItemIds: options.orderItemIds,
      seatIds: options.seatIds,
      subscriptionOrderIds: options.subscriptionOrderIds,
      action: options.action,
      downloadType: DownloadTypeEnumRequestDto.Default,
    };
    // `job-send-by-email` déclenche le MÊME use case backend que `printAsPdf`
    // (LaunchJobPrintPdfTicket) et donc le même refus 409 « une impression est déjà en cours pour
    // la place X ». Sans ce catchError, ce refus échouait en SILENCE : aucun ErrorHandler Angular
    // global n'est fourni dans l'appli, ErrorInterceptor ne traite que les >= 500, et l'appelant
    // souscrit sans gérer l'erreur — le 409 finissait en erreur RxJS non gérée (console + RUM),
    // rien à l'écran. L'utilisateur ne pouvait pas savoir qu'il devait attendre, donc il recliquait
    // (39 rejets en 90 min relevés en production le 10/08/2026). La traduction du code d'erreur
    // existe déjà (errors.launch-job-print-pdf-ticket.print-job-already-in-progress), elle n'était
    // simplement jamais atteinte.
    return this.printClient
      .jobSendByEmail(filter, options?.email, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<LaunchJobPrintPdfTicketResponse>(),
        // The envelope is unwrapped with its generated type so a contract change still fails here,
        // then dropped: the declared return is Observable<void> and no caller reads the body. Left
        // flowing, the signature would claim a value that subscribers typed `() => void` silently
        // ignore — a lie the compiler does not catch across the Observable's variance.
        map(() => undefined),
        catchError(this.handlePrintError.bind(this)),
      );
  }

  public printOneAsBoca(
    printAction: PrintTicketActionEnumRequestDto,
    printStartDate: Date,
    seatId: number,
    orderId: number,
    orderItemIds?: number[],
  ): Observable<SalesBocaTicketResponseDto> {
    return this.printClient
      .printBoca(mapToPrintAction(printAction), orderId, seatId, printStartDate, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<SalesBocaTicketResponseDto>());
  }

  public printAsCard(
    physicalSeatId: number,
    orderSubscriptionId: number,
    printAction: PrintTicketActionEnumRequestDto,
    printStartDate: Date,
  ): Observable<SubscriptionCardResponseDto> {
    return this.printClient
      .printSubscriptionCard(
        physicalSeatId,
        orderSubscriptionId,
        mapToPrintAction(printAction),
        printStartDate,
        Constants.ApiVersion,
      )
      .pipe(this.httpHandlerService.handleApiResponse<SubscriptionCardResponseDto>());
  }

  public getBocaTicketLogos(
    orderId: number,
    orderItemIds?: number[],
    subscriptionOrderIds?: number[],
  ): Observable<BocaLogoResponseDto[]> {
    return this.printingClient
      .getBocaTicketLogos({ orderId, subscriptionOrderIds, orderItemIds }, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<GetBocaTicketLogosResponse>(),
        map(response => response.logos ?? [])
      );
  }

  public previewBoca(seatId: number, orderId?: number): Observable<FileResponse> {
    return this.printClient
      .bocaPreview(null, DownloadTypeEnumRequestDto.Pdf, orderId, null, [seatId], null, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<FileResponse>());
  }

  public previewCard(
    physicalSeatId: number,
    subscriptionOrderId: number,
    printAction: PrintTicketActionEnumRequestDto = PrintTicketActionEnumRequestDto.Print,
  ): Observable<FileResponse> {
    return this.printClient
      .getSeatsSubscriptionCardsPreview(
        physicalSeatId,
        subscriptionOrderId,
        mapToPrintAction(printAction),
        Constants.ApiVersion,
      )
      .pipe(this.httpHandlerService.handleApiResponse<FileResponse>());
  }

  public getEventSessionScopeJobs(
    options: Omit<GetEventSessionScopePrintJobsRequest, 'ticketType'>,
  ): Observable<EventSessionScopePrintJobResponseDto[]> {
    return this.printingClient
      .getEventSessionScopeJobs(
        {
          action: options.action,
          ticketType: PrintTicketTypeEnumResponseDto.Boca,
          orderId: options.orderId,
          orderItemIds: options.orderItemIds,
          subscriptionOrderIds: options.subscriptionOrderIds,
          seatIds: options.seatIds,
        },
        Constants.ApiVersion,
      )
      .pipe(
        this.httpHandlerService.handleApiResponse<GetEventSessionScopePrintJobsResponse>(),
        map((response) => response.jobs ?? []),
      );
  }

  public getSubscriptionScopeJobs(
    options: Omit<GetSubscriptionScopePrintJobsRequest, 'ticketType'>,
  ): Observable<SubscriptionScopePrintJobResponseDto[]> {
    return this.printingClient
      .getSubscriptionScopeJobs(
        {
          action: options.action,
          ticketType: PrintTicketTypeEnumResponseDto.Card,
          orderId: options.orderId,
          subscriptionOrderIds: options.subscriptionOrderIds,
        },
        Constants.ApiVersion,
      )
      .pipe(
        this.httpHandlerService.handleApiResponse<GetSubscriptionScopePrintJobsResponse>(),
        map((response) => response.jobs ?? []),
      );
  }

  public updateStatusToPrinted(
    seatId: number,
    printAction: PrintTicketActionEnumRequestDto,
    subscriptionOrderId?: number,
  ): Observable<boolean> {
    if (!subscriptionOrderId) {
      return this.orderClient
        .updateOrderSeatStatus(
          seatId,
          printAction == PrintTicketActionEnumRequestDto.Duplicate
            ? OrderSeatStatusEnumRequestDto.Duplicate
            : OrderSeatStatusEnumRequestDto.Printed,
          Constants.ApiVersion,
        )
        .pipe(
          this.httpHandlerService.handleApiResponse<UpdateOrderSeatStatusResponse>(),
          map((response) => response.updated),
        );
    } else {
      return this.printClient
        .endProcessSubscriptionCard(seatId, subscriptionOrderId, mapToPrintAction(printAction), Constants.ApiVersion)
        .pipe(this.httpHandlerService.handleApiResponse<boolean>());
    }
  }

  public updateStatusToEditionStarted(orderItemId: number): Observable<boolean> {
    return this.orderClient
      .updateOrderItemStatus(orderItemId, OrderStatusEnumRequestDto.EditionStarted, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<UpdateOrderItemStatusResponse>(),
        map((response) => response.updated),
      );
  }

  public isUpdateStatusOnPrinted(): boolean {
    return true;
  }

  public notifyBocaPrintError(seatId: number, fiscalNumber: string, errorMessage: string) {
    return this.printClient
      .bocaSendPrintError(
        {
          fiscalNumber,
          seatId,
          errorMessage,
        },
        Constants.ApiVersion,
      )
      .pipe(this.httpHandlerService.handleApiResponse<string>());
  }
}
